import { Button, Modal, Select } from 'antd';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { number, object, string } from 'yup';
import { useQueryUsers } from '@hooks/index';
import { Role } from '@interfaces/userInterface';
import { SuggestionCreateDto } from '@interfaces/suggestionInterface';

type ScheduleReplaceModalProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSubmit: (values: SuggestionCreateDto) => void;
	role: Role;
	scheduleId: number;
	userSuggester: number;
	loading?: boolean;
};

export const ScheduleReplaceModal = ({
	open,
	setOpen,
	onSubmit,
	role,
	scheduleId,
	userSuggester,
	loading
}: ScheduleReplaceModalProps) => {
	const queryParams = role === Role.Glasses ? { page: 1, perPage: 200 } : { role, page: 1, perPage: 100 };
	const { data: usersToReplace } = useQueryUsers(queryParams);

	// Form
	const { handleSubmit, values, setFieldValue, isValid } = useFormik<SuggestionCreateDto>({
		initialValues: {
			role: role as string,
			scheduleId: scheduleId ?? undefined,
			userToSuggest: undefined,
			userSuggester: userSuggester ?? undefined
		},
		onSubmit: onSubmit,
		validationSchema: object({
			role: string().required(),
			scheduleId: number().required(),
			userToSuggest: number().required(),
			userSuggester: number().required()
		})
	});

	const onChangeHandler = (value: number) => {
		setFieldValue('userToSuggest', value);
	};

	const filterOption = (input: string, option?: { value: number; label: string }) => {
		if (!option) {
			return false;
		}
		return option.label.toLowerCase().includes(input.toLowerCase());
	};

	useEffect(() => {
		setFieldValue('role', role);
		setFieldValue('scheduleId', scheduleId);
		setFieldValue('userSuggester', userSuggester);
	}, [role, scheduleId, userSuggester, setFieldValue]);

	return (
		<Modal
			onCancel={() => setOpen(false)}
			footer={
				<div className="flex justify-end">
					<Button type="link" danger htmlType="button" onClick={() => setOpen(false)}>
						Annuler
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						onClick={() => onSubmit(values)}
						disabled={!isValid}
						loading={loading}
					>
						Envoyer la demande de remplacement
					</Button>
				</div>
			}
			width={650}
			open={open}
			title="À quel bénévole souhaitez-vous demander le remplacement ?"
		>
			<form className="flex flex-col gap-2 py-2" noValidate autoComplete="off" onSubmit={handleSubmit}>
				{usersToReplace && (
					<Select
						showSearch
						placeholder="Sélectionnez le bénévole remplaçant"
						onChange={onChangeHandler}
						filterOption={filterOption}
						options={usersToReplace.items
							.filter((user) => user.id !== userSuggester)
							.map((user) => ({
								value: user.id,
								label: user.fullName
							}))}
					/>
				)}
			</form>
		</Modal>
	);
};
